/**
 * Cost-Actuals Source of Truth — admin viewer/editor
 * ====================================================
 * Lists every ingested daily spend point with date, amount, source, category.
 * Allows inline edit + delete. Lives at /admin/cost-actuals.
 * Reads /api/commitment-rebalancer/spend-curve/list and patches/deletes via
 * the same module.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SEOHead from '../../components/SEOHead';
import {
  ArrowLeft, Database, RefreshCw, Save, Trash2, Loader2,
  AlertTriangle, X, Check, ExternalLink, MessageSquare,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const a = Math.abs(n); const sign = n < 0 ? '-' : '';
  if (a >= 1_000) return `${sign}$${a.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
  return `${sign}$${a.toFixed(2)}`;
};

const CATEGORIES = ['compute', 'storage', 'egress', 'refunds', 'other'];
const CAT_COLOR = {
  compute: 'emerald', storage: 'cyan', egress: 'amber', refunds: 'violet', other: 'slate',
};

const PAGE_SIZE = 50;

const CostActuals = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState(''); // category filter
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // {date, category, amount_usd}
  const [saving, setSaving] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchList = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API_URL}/commitment-rebalancer/spend-curve/list`, {
        params: { limit: PAGE_SIZE, skip, ...(filter ? { category: filter } : {}) },
        headers,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load (auth required).');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [skip, filter]);

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const day = editing.date.split('T')[0];
      const payload = {};
      if (editing.amount_usd !== undefined && editing.amount_usd !== '') {
        payload.amount_usd = Number(editing.amount_usd);
      }
      if (editing.category) payload.category = editing.category;
      if (editing.note !== undefined) payload.note = editing.note || '';
      await axios.patch(
        `${API_URL}/commitment-rebalancer/spend-curve/${day}?category=${editing.original_category}`,
        payload,
        { headers },
      );
      setEditing(null);
      await fetchList();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete spend point for ${row.date.split('T')[0]} (${row.category})?`)) return;
    try {
      const day = row.date.split('T')[0];
      await axios.delete(
        `${API_URL}/commitment-rebalancer/spend-curve/${day}?category=${row.category}`,
        { headers },
      );
      await fetchList();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not delete');
    }
  };

  const pageStart = skip + 1;
  const pageEnd = Math.min(skip + PAGE_SIZE, total);
  const hasNext = pageEnd < total;
  const hasPrev = skip > 0;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEOHead
        title="Cost Actuals · Source of Truth · CARTIEAI"
        description="Per-day spend ingested from CUR / Azure CM / GCP Billing Export. Editable. The system of record for the rebalancer's 30/60/90-day math."
        noindex
      />

      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-6">
          <Link to="/commitment-rebalancer" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="cost-actuals-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Rebalancer
          </Link>
        </div>

        <div className="mb-8">
          <Badge className="bg-emerald-500/20 text-emerald-200 mb-3">Admin · Source of Truth</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="cost-actuals-title">
            <Database className="w-7 h-7 inline-block mr-2 text-emerald-400" />
            Cost actuals · system of record
          </h1>
          <p className="text-sm sm:text-base text-slate-700 max-w-2xl">
            Every daily spend point your tenant has ingested. The rebalancer's 30/60/90-day curve reads from here.
            Edit a row to correct a CUR ingestion mistake — your change shows up on the next page load.
          </p>
        </div>

        {/* Filters + pagination */}
        <Card className="border-emerald-500/20 mb-4">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-slate-700 font-medium">Filter category:</span>
              <button
                onClick={() => { setFilter(''); setSkip(0); }}
                className={`px-3 py-1 rounded-full text-xs border ${!filter ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-900 text-slate-200 border-emerald-500/30'}`}
                data-testid="cost-actuals-filter-all"
              >
                All
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => { setFilter(c); setSkip(0); }}
                  className={`px-3 py-1 rounded-full text-xs border ${filter === c ? `bg-${CAT_COLOR[c]}-600 text-white border-${CAT_COLOR[c]}-600` : `bg-slate-900 text-${CAT_COLOR[c]}-700 border-${CAT_COLOR[c]}-300`}`}
                  data-testid={`cost-actuals-filter-${c}`}
                >
                  {c}
                </button>
              ))}
              <span className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                {loading ? 'Loading…' : `${pageStart}-${pageEnd} of ${total}`}
                <Button onClick={fetchList} variant="outline" size="sm" data-testid="cost-actuals-refresh"><RefreshCw className="w-3 h-3" /></Button>
              </span>
            </div>
          </CardContent>
        </Card>

        {err && (
          <Card className="border-rose-500/30 bg-rose-500/10 mb-4">
            <CardContent className="pt-4 text-sm text-rose-200" data-testid="cost-actuals-error">
              {err} <Link to="/login" className="underline ml-2">Log in</Link>
            </CardContent>
          </Card>
        )}

        <Card className="border-emerald-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ingested daily spend</CardTitle>
            <CardDescription className="text-slate-400">Click a row to edit · click trash icon to delete</CardDescription>
          </CardHeader>
          <CardContent>
            {loading && items.length === 0 ? (
              <div className="text-sm text-slate-600 py-6 text-center">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading rows…
              </div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center" data-testid="cost-actuals-empty">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <div className="text-sm text-slate-700 font-medium">No ingested points yet.</div>
                <div className="text-xs text-slate-600 mt-1">
                  Visit <Link to="/commitment-rebalancer" className="text-emerald-300 underline">/commitment-rebalancer</Link> and upload a CSV.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cost-actuals-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 border-b border-emerald-500/20">
                      <th className="py-2 pr-3">Date</th>
                      <th className="py-2 pr-3">Category</th>
                      <th className="py-2 pr-3 text-right">Amount</th>
                      <th className="py-2 pr-3">Note</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">Updated</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const day = row.date?.split('T')[0];
                      const isEditing = editing && editing.date === row.date && editing.original_category === row.category;
                      const color = CAT_COLOR[row.category] || 'slate';
                      return (
                        <tr key={`${row.date}-${row.category}`} className="border-b border-slate-100 hover:bg-slate-900" data-testid={`cost-actuals-row-${day}-${row.category}`}>
                          <td className="py-2 pr-3 tabular-nums text-slate-900">{day}</td>
                          <td className="py-2 pr-3">
                            {isEditing ? (
                              <select
                                value={editing.category}
                                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                className="h-8 px-2 rounded border border-emerald-500/30 text-xs"
                                data-testid="cost-actuals-edit-category"
                              >
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                            ) : (
                              <Badge className={`bg-${color}-100 text-${color}-800`}>{row.category}</Badge>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editing.amount_usd}
                                onChange={(e) => setEditing({ ...editing, amount_usd: e.target.value })}
                                className="h-8 w-28 text-right text-sm"
                                data-testid="cost-actuals-edit-amount"
                              />
                            ) : (
                              <span className="font-medium text-slate-900">{fmtMoney(row.amount_usd)}</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 max-w-[200px]">
                            {isEditing ? (
                              <Input
                                type="text"
                                value={editing.note || ''}
                                onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                                placeholder="Black Friday spike, spot migration…"
                                className="h-8 text-xs"
                                data-testid="cost-actuals-edit-note"
                              />
                            ) : row.note ? (
                              <span className="text-xs text-slate-600 italic flex items-center gap-1" data-testid={`cost-actuals-note-${day}-${row.category}`}>
                                <MessageSquare className="w-3 h-3 text-slate-400" />
                                <span title={row.note}>{row.note.length > 30 ? `${row.note.slice(0, 30)}…` : row.note}</span>
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-3"><code className="text-xs text-slate-600">{row.source}</code></td>
                          <td className="py-2 pr-3 text-xs text-slate-600">{row.updated_at?.split('T')[0]}</td>
                          <td className="py-2 text-right whitespace-nowrap">
                            {isEditing ? (
                              <>
                                <Button onClick={saveEdit} size="sm" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2" data-testid="cost-actuals-save">
                                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                </Button>
                                <Button onClick={() => setEditing(null)} size="sm" variant="outline" className="h-7 px-2 ml-1" data-testid="cost-actuals-cancel">
                                  <X className="w-3 h-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  onClick={() => setEditing({ ...row, original_category: row.category })}
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2"
                                  data-testid={`cost-actuals-edit-${day}-${row.category}`}
                                >
                                  Edit
                                </Button>
                                <Button
                                  onClick={() => deleteRow(row)}
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 ml-1 text-rose-300 border-rose-500/40 hover:bg-rose-500/10"
                                  data-testid={`cost-actuals-delete-${day}-${row.category}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 text-xs text-slate-600">
              <span>{pageStart}-{pageEnd} of {total}</span>
              <div className="space-x-2">
                <Button onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} disabled={!hasPrev || loading} variant="outline" size="sm" data-testid="cost-actuals-prev">Prev</Button>
                <Button onClick={() => setSkip(skip + PAGE_SIZE)} disabled={!hasNext || loading} variant="outline" size="sm" data-testid="cost-actuals-next">Next</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-500/20 bg-slate-900 text-slate-100 mt-4">
          <CardContent className="pt-4 text-xs text-slate-400">
            Tip: only <Badge className="bg-emerald-500/20 text-emerald-300 mx-1">compute</Badge> points are commitment-eligible.
            Storage and egress are reported separately — folding them into your SP/RI math is a common over-counting mistake.
            <a href="/api/commitment-rebalancer/spend-curve/status" target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline inline-flex items-center gap-1 ml-2">
              View status JSON <ExternalLink className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CostActuals;
